/**
 * Frequent-refresh guard, ported from the legacy scripts/shared/refresh_util.ts.
 *
 * Reloading the same page three times inside ten seconds looks like scripted
 * traffic rather than a person, so the visitor is sent to the reCAPTCHA page and
 * returned to where they were once they pass it. Keys and thresholds match the
 * legacy implementation so both portals share one counter and one return URL.
 *
 * Started only on the static page (see index.tsx); the Razor host page runs the legacy one.
 */
var STORAGE_KEY = '__Refresh_Page_Key';
var RETURN_URL_KEY = '__refresh_return_url__';
var FROM_REFRESH_KEY = 'isFromRefresh';
var REFRESH_PAGE_HASH = '#/account/refreshPage';
var MIN_REFRESH_TIME_INTERVAL = 10000;
var MAX_ALLOW_REFRESH_COUNT = 3;
/** sessionStorage throws in private mode and can be blocked outright, so never let it break boot. */
function read() {
    try {
        return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
    }
    catch (e) {
        return {};
    }
}
function write(model) {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(model));
    }
    catch (e) {
        // no-op: the guard degrades to "never triggers" rather than blocking the app
    }
}
/**
 * Clears the tally so a visitor returning from the verification page is not sent
 * straight back. The legacy portal got this for free because the counter reset on
 * the next full page load; here the return is a hash change within one document.
 */
export function clearRefreshRecord() {
    try {
        window.sessionStorage.removeItem(STORAGE_KEY);
    }
    catch (e) {
        // nothing to clear if storage is unavailable
    }
}
export function startRefreshGuard() {
    // The verification page itself must not count towards the tally.
    if (window.location.hash.indexOf(REFRESH_PAGE_HASH) > -1) {
        return;
    }
    var model = read();
    var isSamePage = model.lastReviewPage === window.location.href;
    var isShortRefresh = Boolean(isSamePage && model.lastRefreshDate &&
        Date.now() - model.lastRefreshDate < MIN_REFRESH_TIME_INTERVAL);
    var isFrequentRefresh = isShortRefresh && (model.count || 0) >= MAX_ALLOW_REFRESH_COUNT;
    // Reset once the burst is over, or once it has already been acted on.
    var reset = !isShortRefresh || isFrequentRefresh;
    write({
        count: reset ? 0 : (model.count || 0) + 1,
        lastRefreshDate: Date.now(),
        lastReviewPage: window.location.href
    });
    if (!isFrequentRefresh) {
        return;
    }
    try {
        window.sessionStorage.setItem(RETURN_URL_KEY, window.location.href);
        window.sessionStorage.setItem(FROM_REFRESH_KEY, 'true');
    }
    catch (e) {
        // Without a stored return URL the page falls back to the dashboard.
    }
    window.location.href = "" + window.location.pathname + REFRESH_PAGE_HASH;
}
